import { useCallback, useEffect, useState, ReactNode } from "react";
import { useDispatch } from "react-redux";

import {
  PostalAddressKind,
  FreeFormPostalAddress,
  PaymentSourceKind,
  Gender,
} from "@evergiving/common/dist/es2015/pledge";

import { RootState } from "@/lib/redux/types";
import { setPartialState } from "@/lib/redux/actions";

function SetFormActions({
  children,
  settings,
}: {
  children: ReactNode;
  settings: Partial<RootState>;
}) {
  const dispatch = useDispatch();

  const handleClick = useCallback(() => {
    dispatch(setPartialState(settings));
  }, [settings, dispatch]);

  return (
    <button type="button" onClick={handleClick}>
      {children}
    </button>
  );
}

const address1: FreeFormPostalAddress = {
  kind: PostalAddressKind.FreeForm,
  element2: "TH",
  element3: "10330",
  element4: "Bangkok",
  element5: undefined,
  element6: "Pathum Wan District",
  element7: undefined,
  element8: "Rong Muang",
  element9: undefined,
  element10: undefined,
  element11: "ซอย จรัสเมือง",
  element12: "113",
  element13: undefined,
  element14: undefined,
  element15: undefined,
};

const baseCardPayment: Partial<RootState> = {
  firstName: { version: 1, value: "Bill" },
  lastName: { version: 1, value: "Paid" },
  postalAddress: address1,
  paymentSourceKind: PaymentSourceKind.CreditCard,
  cardHolder: { version: 1, value: "Bill Paid" },
  cardExpiry: { version: 1, value: "2025-12" },
  cardVerificationCode: { version: 1, value: "123" },
  gender: { version: 1, value: "male" as Gender },
  profession: { version: 1, value: "dummy" },
  title: { version: 1, value: "Most egregious" },
  dateOfBirth: { version: 1, value: "1960-04-01" },
  phoneNumber: { version: 1, value: "+6621212121" },
  emailAddress: { version: 1, value: "test@example.com" },
  directDebitAccountHolder: { version: 1, value: "Bill Paid" },
  directDebitIBAN: { version: 1, value: "GB33BUKB20201555555555" },
  belfioreCode: { version: 1, value: "H501" },

  element3: { version: 1, value: "10330" },
  element4: { version: 1, value: "Bangkok" },
  element6: { version: 1, value: "Pathum Wan District" },
  element8: { version: 1, value: "Rong Muang" },
  element11: { version: 1, value: "ซอย จรัสเมือง" },
  element12: { version: 1, value: "113" },

  termsAndConditionsConsent: true,

  errors: {},
};

const card3ds2 = {
  ...baseCardPayment,
  cardNumber: { version: 2, value: "4000000000003220" },
};

const card3ds = {
  ...baseCardPayment,
  cardNumber: { version: 2, value: "4000000000003063" },
};

const card3dsDeclined = {
  ...baseCardPayment,
  cardNumber: { version: 2, value: "4000008400001629" },
};

const noAuth = {
  ...baseCardPayment,
  cardNumber: { version: 2, value: "378282246310005" },
};

const fraudulent = {
  ...baseCardPayment,
  cardNumber: { version: 2, value: "4100000000000019" },
};

const cardDeclined = {
  ...baseCardPayment,
  cardNumber: { version: 2, value: "4000000000000002" },
};

const insufficientFunds = {
  ...baseCardPayment,
  cardNumber: { version: 2, value: "4000000000009995" },
};

const lostCard = {
  ...baseCardPayment,
  cardNumber: { version: 2, value: "4000000000009987" },
};

const stolenCard = {
  ...baseCardPayment,
  cardNumber: { version: 2, value: "4000000000009979" },
};

const expiredCard = {
  ...baseCardPayment,
  cardNumber: { version: 2, value: "4000000000000069" },
};

const incorrectCvc = {
  ...baseCardPayment,
  cardNumber: { version: 2, value: "4000000000000127" },
};

const processingError = {
  ...baseCardPayment,
  cardNumber: { version: 2, value: "4000000000000119" },
};

const chargeSuccessful = {
  ...baseCardPayment,
  cardNumber: { version: 2, value: "4000000000000077" },
};

function DevActionsBody() {
  return (
    <div
      style={{
        position: "fixed",
        right: 20,
        bottom: 20,
        zIndex: 2,
        display: "flex",
        flexDirection: "column",
        alignItems: "flex-start",
        backgroundColor: "#000c",
        color: "#fff",
        padding: 15,
      }}
    >
      <SetFormActions settings={card3ds2}>Need 3D Secure 2</SetFormActions>
      <SetFormActions settings={card3ds}>Need 3D Secure</SetFormActions>
      <SetFormActions settings={card3dsDeclined}>
        Need 3D Secure, declined
      </SetFormActions>
      <SetFormActions settings={noAuth}>
        No Authentication required
      </SetFormActions>
      <SetFormActions settings={fraudulent}>Fraudulent</SetFormActions>
      <SetFormActions settings={cardDeclined}>Card Declined</SetFormActions>
      <SetFormActions settings={insufficientFunds}>
        Insufficient Funds
      </SetFormActions>
      <SetFormActions settings={lostCard}>Lost Card</SetFormActions>
      <SetFormActions settings={stolenCard}>Stolen Card</SetFormActions>
      <SetFormActions settings={expiredCard}>Expired Card</SetFormActions>
      <SetFormActions settings={incorrectCvc}>Incorrect CVC</SetFormActions>
      <SetFormActions settings={processingError}>
        Processing Error
      </SetFormActions>
      <SetFormActions settings={chargeSuccessful}>
        Charge Successful
      </SetFormActions>
    </div>
  );
}

export function DevActions() {
  const [visible, setVisible] = useState<boolean>(true);

  const keydownHandler = useCallback((event: KeyboardEvent) => {
    if (event.ctrlKey && event.code === "KeyI") {
      setVisible((visibleFromState) => !visibleFromState);
    }
  }, []);

  useEffect(() => {
    window.addEventListener("keydown", keydownHandler);
    return () => {
      window.removeEventListener("keydown", keydownHandler);
    };
  }, [keydownHandler]);

  if (process.env.NODE_ENV === "production") {
    return null;
  }

  if (!visible) {
    return null;
  }
  return <DevActionsBody />;
}
